import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileInvoice, faBuilding, faDollarSign, faUsers,
  faChartPie, faChartLine, faCalendarAlt, faSyncAlt,
  faExclamationTriangle, faCheckCircle, faClock
} from '@fortawesome/free-solid-svg-icons';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
import Dexie from 'dexie';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Cache Dexie (24h)
const db = new Dexie('StatePlacementFactCache');
db.version(1).stores({ stats: 'key, data, timestamp' });
const CACHE_DURATION = 24 * 60 * 60 * 1000;

async function getCached(key) {
  try {
    const entry = await db.stats.get(key);
    if (!entry) return null;
    if (Date.now() - entry.timestamp > CACHE_DURATION) return null;
    return entry.data;
  } catch { return null; }
}
async function setCached(key, data) {
  await db.stats.put({ key, data, timestamp: Date.now() });
}

const API_URL = `${API_CONFIG2.apiUrl}placement/api_factures_stats.php`;

const StatePlacementFact = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [dataSource, setDataSource] = useState('cache');
  const [annee, setAnnee] = useState(new Date().getFullYear());

  const abortControllerRef = useRef(null);
  const isMounted = useRef(true);

  const fetchStats = useCallback(async (forceRefresh = false) => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    if (forceRefresh) setRefreshing(true);
    else setLoading(true);

    const cacheKey = `stats_${annee}`;

    try {
      if (!forceRefresh) {
        const cached = await getCached(cacheKey);
        if (cached) {
          setStats(cached);
          setDataSource('cache');
          setLoading(false);
          setRefreshing(false);
          return;
        }
      }

      const url = `${API_URL}?action=stats&annee=${annee}&unique=${new Date().getTime()}`;
      const response = await fetch(url, { signal: controller.signal });
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Réponse non-JSON: ${text.substring(0, 100)}`);
      }
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (data.status === 'success') {
        setStats(data.data);
        setDataSource(forceRefresh ? 'live (actualisé)' : 'live');
        await setCached(cacheKey, data.data);
        setError(null);
      } else {
        throw new Error(data.message || 'Erreur inconnue');
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      setError(err.message);
      setStats(null);
    } finally {
      if (isMounted.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [annee]);

  useEffect(() => {
    isMounted.current = true;
    fetchStats();
    return () => {
      isMounted.current = false;
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, [fetchStats]);

  const formatCurrency = (value) => parseFloat(value).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' USD';

  if (loading && !stats) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="text-secondary mt-2">Chargement des statistiques...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-4 text-danger">
        <FontAwesomeIcon icon={faExclamationTriangle} size="2x" className="mb-2" />
        <p>{error}</p>
        <button className="btn btn-outline-primary" onClick={() => fetchStats(true)}>
          <FontAwesomeIcon icon={faSyncAlt} className="me-2" /> Réessayer
        </button>
      </div>
    );
  }

  if (!stats) return null;

  // Préparation des données graphiques
  const general = stats.general || {};
  const byStatut = stats.byStatut || [];
  const evolution = stats.evolution || [];
  const topEntreprises = stats.topEntreprises || [];
  const byContrat = stats.byContrat || [];
  const repartitionEntreprises = stats.repartitionEntreprises || [];

  // Doughnut - Répartition par statut
  const statutLabels = byStatut.map(s => s.statut);
  const statutData = byStatut.map(s => parseFloat(s.montant));
  const statutColors = {
    'En attente': '#f59e0b',
    'Payée': '#4ade80',
    'Annulée': '#ef4444'
  };
  const statutBackgroundColors = byStatut.map(s => statutColors[s.statut] || '#94a3b8');

  const doughnutData = {
    labels: statutLabels,
    datasets: [{
      data: statutData,
      backgroundColor: statutBackgroundColors,
      borderColor: '#0b1428',
      borderWidth: 2,
      hoverOffset: 10,
    }]
  };
  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: { color: '#e0e7ff', boxWidth: 12, usePointStyle: true, padding: 16 },
        position: 'bottom'
      },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
            const pct = total ? ((ctx.parsed / total) * 100).toFixed(1) : 0;
            return `${ctx.label}: ${formatCurrency(ctx.parsed)} (${pct}%)`;
          }
        }
      }
    },
    cutout: '70%',
  };

  // Bar - Top entreprises
  const barLabels = topEntreprises.map(e => e.nomEntreprise);
  const barDataValues = topEntreprises.map(e => parseFloat(e.total_ttc));
  const barData = {
    labels: barLabels,
    datasets: [{
      label: 'Montant TTC (USD)',
      data: barDataValues,
      backgroundColor: 'rgba(79, 140, 255, 0.7)',
      borderRadius: 6,
      hoverBackgroundColor: 'rgba(79, 140, 255, 0.9)',
    }]
  };
  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => formatCurrency(ctx.parsed.y)
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#94a3b8' }
      },
      y: {
        grid: { color: 'rgba(255,255,255,0.05)' },
        ticks: {
          color: '#94a3b8',
          callback: (value) => formatCurrency(value)
        }
      }
    }
  };

  // Line - Evolution mensuelle
  const lineLabels = evolution.map(e => e.mois);
  const lineDataValues = evolution.map(e => parseFloat(e.total_ttc));
  const lineData = {
    labels: lineLabels,
    datasets: [{
      label: 'Montant TTC (USD)',
      data: lineDataValues,
      borderColor: '#4f8cff',
      backgroundColor: 'rgba(79, 140, 255, 0.1)',
      fill: true,
      tension: 0.3,
      pointBackgroundColor: '#4f8cff',
      pointBorderColor: '#0b1428',
      pointBorderWidth: 2,
    }]
  };
  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => formatCurrency(ctx.parsed.y)
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#94a3b8' }
      },
      y: {
        grid: { color: 'rgba(255,255,255,0.05)' },
        ticks: {
          color: '#94a3b8',
          callback: (value) => formatCurrency(value)
        }
      }
    }
  };

  return (
    <>
      <style>{`
        .stats-wrapper {
          background: radial-gradient(ellipse at 20% 30%, #132a4a, #0a0f1f);
          padding: 1.5rem;
          border-radius: 24px;
          min-height: 80vh;
          margin-top: 1.5rem;
        }
        .stats-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
          margin-bottom: 1.8rem;
        }
        .stats-header h4 {
          font-weight: 700;
          color: #e8edff;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .stats-header h4 .icon {
          color: #4f8cff;
        }
        .badge-source {
          font-size: 0.7rem;
          padding: 4px 14px;
          border-radius: 40px;
          background: rgba(79, 140, 255, 0.12);
          color: #4f8cff;
          border: 1px solid rgba(79, 140, 255, 0.1);
          font-weight: 500;
          white-space: nowrap;
        }
        .btn-glass {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          color: #e8edff;
          border-radius: 40px;
          padding: 0.4rem 1rem;
          transition: all 0.2s;
          font-size: 0.9rem;
          backdrop-filter: blur(4px);
          cursor: pointer;
        }
        .btn-glass:hover {
          background: rgba(255,255,255,0.08);
          border-color: rgba(255,255,255,0.12);
        }
        .btn-glass:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .kpi-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 1rem;
          margin-bottom: 2rem;
        }
        .kpi-card {
          background: rgba(255,255,255,0.03);
          backdrop-filter: blur(8px);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 16px;
          padding: 1rem;
          text-align: center;
          transition: all 0.3s;
        }
        .kpi-card:hover {
          background: rgba(255,255,255,0.06);
          transform: translateY(-2px);
        }
        .kpi-card .kpi-icon {
          font-size: 1.6rem;
          color: #4f8cff;
          margin-bottom: 0.3rem;
          display: block;
        }
        .kpi-card .kpi-value {
          font-size: 1.6rem;
          font-weight: 700;
          color: #fff;
          line-height: 1.2;
        }
        .kpi-card .kpi-label {
          font-size: 0.85rem;
          color: #a5b4d9;
          margin-top: 0.2rem;
        }
        .charts-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.5rem;
          margin-top: 1.5rem;
        }
        .chart-card {
          background: rgba(255,255,255,0.03);
          backdrop-filter: blur(8px);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 20px;
          padding: 1.2rem;
        }
        .chart-card .chart-title {
          font-weight: 600;
          color: #a5b4d9;
          font-size: 0.95rem;
          margin-bottom: 0.8rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .chart-container {
          height: 240px;
          position: relative;
        }
        .chart-container.full-height {
          height: 280px;
        }
        @media (max-width: 992px) {
          .charts-grid {
            grid-template-columns: 1fr;
          }
        }
        @media (max-width: 768px) {
          .stats-wrapper { padding: 1rem; }
          .kpi-grid { grid-template-columns: 1fr 1fr; }
          .chart-container { height: 200px; }
        }
        @media (max-width: 480px) {
          .kpi-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      <div className="stats-wrapper">
        <div className="stats-header">
          <h4>
            <FontAwesomeIcon icon={faChartPie} className="icon" />
            Tableau de bord des factures
          </h4>
          <div className="d-flex gap-2 align-items-center">
            <span className="badge-source">{dataSource === 'cache' ? '📦 Cache' : '🔄 Live'}</span>
            <button className="btn-glass" onClick={() => fetchStats(true)} disabled={refreshing}>
              <FontAwesomeIcon icon={faSyncAlt} spin={refreshing} className="me-1" />
              {refreshing ? '...' : 'Actualiser'}
            </button>
          </div>
        </div>

        {/* Filtre année */}
        <div className="d-flex align-items-center gap-2 mb-3">
          <FontAwesomeIcon icon={faCalendarAlt} className="text-secondary" />
          <span className="text-secondary">Année :</span>
          <select
            className="form-select form-select-sm"
            style={{ width: 'auto', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', color: '#e8edff', borderRadius: '40px', padding: '0.2rem 1rem' }}
            value={annee}
            onChange={(e) => setAnnee(parseInt(e.target.value))}
          >
            {[...Array(5)].map((_, i) => {
              const y = new Date().getFullYear() - i;
              return <option key={y} value={y}>{y}</option>;
            })}
          </select>
        </div>

        {/* KPIs */}
        <div className="kpi-grid">
          <div className="kpi-card">
            <FontAwesomeIcon icon={faFileInvoice} className="kpi-icon" />
            <div className="kpi-value">{general.total_factures || 0}</div>
            <div className="kpi-label">Factures</div>
          </div>
          <div className="kpi-card">
            <FontAwesomeIcon icon={faBuilding} className="kpi-icon" style={{ color: '#4ade80' }} />
            <div className="kpi-value">{general.total_entreprises || 0}</div>
            <div className="kpi-label">Entreprises</div>
          </div>
          <div className="kpi-card">
            <FontAwesomeIcon icon={faDollarSign} className="kpi-icon" style={{ color: '#f59e0b' }} />
            <div className="kpi-value">{formatCurrency(general.total_ttc || 0)}</div>
            <div className="kpi-label">Montant total TTC</div>
          </div>
          <div className="kpi-card">
            <FontAwesomeIcon icon={faUsers} className="kpi-icon" style={{ color: '#4f8cff' }} />
            <div className="kpi-value">{formatCurrency(general.moyenne_ttc || 0)}</div>
            <div className="kpi-label">Moyenne par facture</div>
          </div>
        </div>

        {/* Graphiques */}
        <div className="charts-grid">
          <div className="chart-card">
            <div className="chart-title">
              <FontAwesomeIcon icon={faChartPie} /> Répartition par statut
            </div>
            <div className="chart-container">
              <Doughnut data={doughnutData} options={doughnutOptions} />
            </div>
          </div>
          <div className="chart-card">
            <div className="chart-title">
              <FontAwesomeIcon icon={faChartLine} /> Évolution mensuelle (TTC)
            </div>
            <div className="chart-container">
              <Line data={lineData} options={lineOptions} />
            </div>
          </div>
          <div className="chart-card">
            <div className="chart-title">
              <FontAwesomeIcon icon={faBuilding} /> Top 5 entreprises (TTC)
            </div>
            <div className="chart-container">
              <Bar data={barData} options={barOptions} />
            </div>
          </div>
          <div className="chart-card">
            <div className="chart-title">
              <FontAwesomeIcon icon={faFileInvoice} /> Répartition par type de contrat
            </div>
            <div className="chart-container">
              <Bar
                data={{
                  labels: byContrat.map(c => c.type_contrat || 'Non défini'),
                  datasets: [{
                    label: 'Montant TTC (USD)',
                    data: byContrat.map(c => parseFloat(c.total_ttc)),
                    backgroundColor: 'rgba(74, 222, 128, 0.7)',
                    borderRadius: 6,
                    hoverBackgroundColor: 'rgba(74, 222, 128, 0.9)',
                  }]
                }}
                options={{
                  ...barOptions,
                  plugins: { ...barOptions.plugins, legend: { display: false } }
                }}
              />
            </div>
          </div>
        </div>

        {/* Répartition par entreprise (tableau) */}
        {repartitionEntreprises.length > 0 && (
          <div className="chart-card mt-3">
            <div className="chart-title">
              <FontAwesomeIcon icon={faBuilding} /> Répartition des montants par entreprise
            </div>
            <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
              <table className="table table-borderless table-hover" style={{ color: '#e8edff', fontSize: '0.9rem' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    <th>Entreprise</th>
                    <th style={{ textAlign: 'right' }}>Montant TTC</th>
                  </tr>
                </thead>
                <tbody>
                  {repartitionEntreprises.map((e, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                      <td>{e.nomEntreprise}</td>
                      <td style={{ textAlign: 'right', fontWeight: '500', color: '#4ade80' }}>
                        {formatCurrency(e.total_ttc)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default StatePlacementFact;